import React from 'react';
import { Tag } from 'antd';
import { get, isArray, isPlainObject } from 'lodash';
import { addDays, asString, formatDateTime, normalizeRiskQualitative, parseInnerTable, toSafeNumber } from './trans.effect';

export type RrzuBasicLogTable =
  | { key: string; title: string; kind: 'kv'; rows: Array<{ key: React.Key; label: string; value: any }> }
  | { key: string; title: string; kind: 'inner'; inner: { title?: string; headers: any[]; keys: string[]; rows: any[] } }
  | { key: string; title: string; kind: 'platform'; rows: any[] };

export interface RrzuBasicViewModel {
  header: {
    reportTitle: string;
    createdTime: string;
    validTime: string;
  };
  risk: {
    qualitativeText: string;
    qualitativeColor: string;
    indicatorTags: React.ReactNode[];
    riskResultText: string;
    riskAdviseText: string;
  };
  logTables: RrzuBasicLogTable[];
}

const toIndicatorTag = (label: string, value: any) => {
  const v = asString(value);
  const pass = v === '是' || v === '1' || v.toLowerCase() === 'true';
  const fail = v === '否' || v === '0' || v.toLowerCase() === 'false';
  const text = pass ? `${label}通过` : fail ? `${label}未通过` : `${label}${v}`;
  const color = pass ? 'gold' : fail ? 'red' : 'default';
  return React.createElement(Tag, { color }, text);
};

export const toRrzuBasicViewModel = (response: any): RrzuBasicViewModel | null => {
  const payload = get(response, 'data');
  if (!isPlainObject(payload)) return null;

  const baseInfo = get(payload, 'base_info') || {};
  const reportTitle = asString(get(baseInfo, 'title'));
  const createdTime = asString(get(baseInfo, 'created_time'));
  const validTime = asString(get(baseInfo, 'valid_time'));

  const resultInfo = get(payload, 'result_info') || {};
  const riskQualitativeObj = get(resultInfo, 'risk_qualitative');
  const riskResultObj = get(resultInfo, 'risk_result');
  const riskAdviseObj = get(resultInfo, 'risk_advise');

  const qualitativeText = normalizeRiskQualitative(asString(get(riskQualitativeObj, 'value') ?? riskQualitativeObj));
  const qualitativeColor = qualitativeText.includes('通过') ? '#52c41a' : qualitativeText.includes('预收') ? '#faad14' : '#f5222d';

  const riskResultText = asString(get(riskResultObj, 'value') ?? riskResultObj);
  const riskAdviseText = asString(get(riskAdviseObj, 'value') ?? riskAdviseObj);

  const userInfo = get(payload, 'user_info');
  const indicatorTags = isArray(userInfo)
    ? userInfo
        .filter((r: any) => asString(r?.label) !== '')
        .map((r: any, idx: number) => {
          const label = asString(r.label) === '活体检测' ? '人脸识别' : asString(r.label);
          return React.createElement(React.Fragment, { key: idx }, toIndicatorTag(label, r?.value));
        })
    : [];

  const logInfo = get(payload, 'log_info') || {};

  const logTables: RrzuBasicLogTable[] = [];

  const pushKv = (key: string, title: string, value: any) => {
    const rows = isArray(value)
      ? value
          .filter((r: any) => asString(r?.label) !== '')
          .map((r: any, idx: number) => ({ key: r.id ?? idx, label: asString(r.label), value: r.value }))
      : [];
    logTables.push({ key, title, kind: 'kv', rows });
  };

  pushKv('fraud_risk_info', '欺诈风险', get(logInfo, 'fraud_risk_info'));
  pushKv('solvency_info', '偿还能力', get(logInfo, 'solvency_info'));

  Object.entries(logInfo).forEach(([key, val]) => {
    if (key === 'fraud_risk_info' || key === 'solvency_info') return;
    if (!val) return;
    if (isArray(val) && val.length && isPlainObject(val[0]) && 'label' in (val[0] as any)) {
      pushKv(key, key, val);
    }
  });

  const judicialList = get(payload, 'judicial_risk_info');
  if (isArray(judicialList)) {
    const first = judicialList[0];
    const innerVal = first ? first.value : undefined;
    const inner = parseInnerTable(innerVal);
    const fallback = {
      title: '司法案件详情',
      headers: ['序号', '风险板块', '法院', '案号', '立案日期', '匹配度', '详情'],
      keys: ['id', 'query_item', 'court_name', 'case_no', 'case_at', 'match_degree', 'detail'],
      rows: [],
    };
    logTables.push({ key: 'judicial_risk_info', title: '司法案件', kind: 'inner', inner: inner || fallback });
  }

  const platformList = get(payload, 'platform_trade_info');
  if (isArray(platformList)) {
    const first = platformList[0];
    const rowsVal = first ? first.value : undefined;
    const rows = isArray(rowsVal) ? rowsVal.map((r: any, i: number) => (isPlainObject(r) ? { ...r, key: r.key ?? i } : { key: i })) : [];
    logTables.push({ key: 'platform_trade_info', title: '平台交易数据', kind: 'platform', rows });
  }

  const tableOrderWeight: Record<string, number> = {
    fraud_risk_info: 10,
    solvency_info: 20,
    platform_trade_info: 80,
    judicial_risk_info: 90,
  };

  const sortedLogTables = logTables
    .map((t, idx) => ({ ...t, __idx: idx } as any))
    .sort((a: any, b: any) => {
      const wa = tableOrderWeight[a.key] ?? 999;
      const wb = tableOrderWeight[b.key] ?? 999;
      if (wa !== wb) return wa - wb;
      return a.__idx - b.__idx;
    })
    .map((t: any) => {
      const { __idx, ...rest } = t;
      return rest as RrzuBasicLogTable;
    });

  return {
    header: { reportTitle, createdTime, validTime },
    risk: { qualitativeText, qualitativeColor, indicatorTags, riskResultText, riskAdviseText },
    logTables: sortedLogTables,
  };
};

export interface SolvencyLevel {
  text: string;
  color: string;
}

export const toSolvencyLevel = (value: any): SolvencyLevel | null => {
  const raw = asString(value).trim();
  if (!raw) return null;

  const byText: Record<string, SolvencyLevel> = {
    无: { text: '无', color: '#8c8c8c' },
    少: { text: '少', color: '#1890ff' },
    中: { text: '中', color: '#faad14' },
    多: { text: '多', color: '#f5222d' },
  };
  if (raw in byText) return byText[raw];

  const normalized = raw.replace(/[～〜]/g, '~').replace(/[－—]/g, '-');
  const nums = normalized.match(/-?\d+(?:\.\d+)?/g) || [];
  if ((normalized.includes('~') || (normalized.includes('-') && !normalized.startsWith('-'))) && nums.length >= 2) return null;
  const num = nums.length ? Math.max(...nums.map((n) => toSafeNumber(n, NaN)).filter((n) => Number.isFinite(n))) : NaN;
  if (!Number.isFinite(num)) return null;

  if (num <= 0) return byText['无'];
  if (num === 1) return byText['少'];
  if (num === 2) return byText['中'];
  return byText['多'];
};

export interface BuerOverdueViewModel {
  header: {
    createdTime: string;
    validTime: string;
  };
  resultCode: any;
  overdueStatus: {
    maxOverdueDays: any;
    maxOverdueAmt: any;
    latestOverdueTime: any;
  };
  performanceStatus: {
    countPerformance: any;
    maxPerformanceAmt: any;
    latestPerformanceTime: any;
  };
  agencyStatus: {
    currentlyOverdue: any;
    currentlyPerformance: any;
    accExc: any;
    accSleep: any;
  };
}

export const toBuerOverdueViewModel = (data: any): BuerOverdueViewModel | null => {
  if (!data || !isPlainObject(data)) return null;
  const timestamp = get(data, 'timestamp') ?? get(data, 'resp_data.timestamp') ?? get(data, 'created_time') ?? get(data, 'resp_data.created_time');
  const wrapped = get(data, 'resp_data');
  const info = isPlainObject(wrapped) ? wrapped : data;
  if (!isPlainObject(info)) return null;

  return {
    header: {
      createdTime: formatDateTime(timestamp),
      validTime: addDays(timestamp, 7),
    },
    resultCode: get(info, 'result_code') ?? get(info, 'resultCode') ?? get(info, 'result'),
    overdueStatus: {
      maxOverdueDays: get(info, 'max_overdue_days') ?? get(info, 'maxOverdueDays'),
      maxOverdueAmt: get(info, 'max_overdue_amt') ?? get(info, 'maxOverdueAmt'),
      latestOverdueTime: get(info, 'latest_overdue_time') ?? get(info, 'latestOverdueTime'),
    },
    performanceStatus: {
      countPerformance: get(info, 'count_performance') ?? get(info, 'countPerformance'),
      maxPerformanceAmt: get(info, 'max_performance_amt') ?? get(info, 'maxPerformanceAmt'),
      latestPerformanceTime: get(info, 'latest_performance_time') ?? get(info, 'latestPerformanceTime'),
    },
    agencyStatus: {
      currentlyOverdue: get(info, 'currently_overdue') ?? get(info, 'currentlyOverdue'),
      currentlyPerformance: get(info, 'currently_performance') ?? get(info, 'currentlyPerformance'),
      accExc: get(info, 'acc_exc') ?? get(info, 'accExc'),
      accSleep: get(info, 'acc_sleep') ?? get(info, 'accSleep'),
    },
  };
};

export interface BuerXingyaoViewModel {
  header: {
    reportTitle: string;
    createdTime: string;
    validTime: string;
  };
  risk: {
    verifyText: string;
    verifyColor: string;
    score: number | null;
  };
  riskTags: Array<string | { risk?: string; title?: string; detail?: string; desc?: string; explain?: string; description?: string }>;
  riskTagRows: Array<{ key: string; label: string; detail: string }>;
  identity: {
    networkTripleElements: any;
    identityTwoElements: any;
    timeOnline: any;
  };
  riskListCheck: Record<string, any>;
  demand: Record<string, any>;
  loanS: Record<string, any>;
  loanF: Record<string, any>;
  loanH: Record<string, any>;
  overdueHistory: Record<string, any>;
  rentHistory: Record<string, any>;
  relevanceRisk: Record<string, any>;
  courtRiskList: any[];
}

/**
 * 将天狼星报告「星耀版」转换成页面可用的 ViewModel
 */
export const toBuerXingyaoViewModel = (data: any): BuerXingyaoViewModel | null => {
  if (!data || !isPlainObject(data)) return null;
  const timestamp =
    get(data, 'timestamp') ??
    get(data, 'resp_data.timestamp') ??
    get(data, 'resp_data.base_info.created_time') ??
    get(data, 'created_time') ??
    get(data, 'base_info.created_time');
  const wrapped = get(data, 'resp_data');
  const info = isPlainObject(wrapped) ? wrapped : data;
  if (!isPlainObject(info)) return null;

  const scoreRaw = get(info, 'score_norm_explain') ?? get(info, 'score');
  const scoreNum = toSafeNumber(scoreRaw, NaN);
  const score = Number.isFinite(scoreNum) ? scoreNum : null;

  const verifyRaw = asString(get(info, 'verify_recomment')).trim();
  const verifyTextBase = verifyRaw ? verifyRaw : score === null ? '' : score > 530 ? '建议通过' : score > 430 ? '建议审核' : '建议拒绝';
  const verifyText = verifyTextBase && !verifyTextBase.startsWith('建议') ? `建议${verifyTextBase}` : verifyTextBase;
  const verifyColor = score === null ? '#d9d9d9' : score > 530 ? '#1890ff' : score > 430 ? '#faad14' : '#f5222d';

  const riskTagsRaw = get(info, 'hit_risk_tagging') as any;
  const riskTags = isArray(riskTagsRaw)
    ? (riskTagsRaw as any[]).filter((v) => (typeof v === 'string' ? v.trim() !== '' : isPlainObject(v)))
    : [];

  const normalize = (v: any) => asString(v).trim().replace(/\s+/g, '');
  const riskTagDetailMap: Record<string, string> = {
    命中新增加风险特征规则: '命中报告中未展示的内容，数据的模型评分较低',
    多头查询次数多: '近3个月申请网贷的查询次数较多',
    身份证和手机号关联数据多: '用户的身份证号码下注册了多个手机号，或手机号被多个身份证号码注册',
    号码异常: '手机状态异常、查无此号或注销等异常',
    命中风险关注名单: '命中信贷高风险或租赁关注类名单库',
    近半年内存在逾期记录: '近半年内存在多机构逾期或存在逾期未结清账单',
    司法案件匹配度高: '用户历史上有过失信、犯罪、纠纷等严重影响信用记录的法院案件记录需审核须谨慎',
    近1个月扣款失败次数较多: '近1个月代扣还款失败次数较多',
    年龄过大: '年龄过大',
    年龄过小: '年龄过小',
    近1周申请租赁平台较多: '近1周查询过用户的租赁平台个数较多',
    号码使用时间较短: '号码使用时间较短',
    三要素验证不一致: '姓名、身份证、手机号三要素验证不一致',
    二要素验证不一致: '姓名、身份证验证不一致',
    近1个月网贷平台申请频繁: '近1个月网络助贷平台申请次数较多',
  };

  const getRiskTagDetail = (rawKey: string) => {
    if (rawKey in riskTagDetailMap) return riskTagDetailMap[rawKey];
    const n = normalize(rawKey);
    if (!n) return '';
    for (const [k, detail] of Object.entries(riskTagDetailMap)) {
      if (normalize(k) === n) return detail;
    }
    for (const [k, detail] of Object.entries(riskTagDetailMap)) {
      const kn = normalize(k);
      if (!kn) continue;
      if (n.includes(kn) || kn.includes(n)) return detail;
    }
    return '';
  };

  const riskTagRows = riskTags
    .map((field: any, i: number) => {
      const rawKey = typeof field === 'string' ? asString(field).trim() : asString(field?.risk ?? field?.title).trim();
      if (!rawKey) return null;
      const rawDetail =
        typeof field === 'string' ? '' : asString(field?.detail ?? field?.desc ?? field?.explain ?? field?.description).trim();
      const detail = rawDetail || getRiskTagDetail(rawKey);

      return { key: `${rawKey}-${i}`, label: rawKey, detail };
    })
    .filter(Boolean) as Array<{ key: string; label: string; detail: string }>;

  return {
    header: {
      reportTitle: '天狼星报告星耀版',
      createdTime: formatDateTime(timestamp),
      validTime: addDays(timestamp, 7),
    },
    risk: { verifyText, verifyColor, score },
    riskTags,
    riskTagRows,
    identity: {
      networkTripleElements: get(info, 'ident_info_dict.network_triple_elements'),
      identityTwoElements: get(info, 'ident_info_dict.identity_two_elements'),
      timeOnline: get(info, 'ident_info_dict.time_online'),
    },
    riskListCheck: (get(info, 'risk_list_check') as any) || {},
    demand: (get(info, 'personal_loan_demand') as any) || {},
    loanS: (get(info, 'personal_loan_s') as any) || {},
    loanF: (get(info, 'personal_loan_f') as any) || {},
    loanH: (get(info, 'personal_loan_h') as any) || {},
    overdueHistory: (get(info, 'personal_overdue_history') as any) || {},
    rentHistory: (get(info, 'rent_history') as any) || {},
    relevanceRisk: (get(info, 'relevance_risk_check') as any) || {},
    courtRiskList: (get(info, 'court_risk_info_list') as any[]) || [],
  };
};

export interface RrzuVideoViewModel {
  orderId: string;
  shipText: string;
  shipColor: string;
  cause: string;
  rows: Array<{ key: string; label: string; value: any }>;
}

export const toRrzuVideoViewModel = (response: any): RrzuVideoViewModel | null => {
  const info = get(response, 'data');
  if (!isPlainObject(info)) return null;

  const shipValRaw = get(info, 'is_ship');
  const shipValStr = shipValRaw === undefined || shipValRaw === null ? '' : String(shipValRaw);
  const isShip = shipValStr === '1' || shipValStr.toLowerCase() === 'true';
  const shipText = shipValStr ? (isShip ? '建议发货' : '建议拒绝') : '';
  const shipColor = isShip ? '#52c41a' : '#f5222d';

  const cause = asString(get(info, 'cause'));
  const orderId = asString(get(info, 'order_id'));

  const rows = [
    { key: 'shooting_env', label: '拍摄环境描写', value: asString(get(info, 'shooting_env')) || '-' },
    { key: 'disease_attribute', label: '疾病特征', value: asString(get(info, 'disease_attribute')) || '-' },
    { key: 'is_id', label: '用户是否说出身份证号后四位', value: get(info, 'is_id') },
    { key: 'is_name', label: '用户是否说出姓名', value: get(info, 'is_name') },
    { key: 'has_guidance_detected', label: '是否有他人引导回答', value: get(info, 'has_guidance_detected') },
    { key: 'has_multshows_tensioniple_subjects', label: '视频主体是否有2人及以上', value: get(info, 'has_multshows_tensioniple_subjects') },
  ];

  return { orderId, shipText, shipColor, cause, rows };
};
